import { resendVerificationSchema } from "@closer/shared";
import { Button, Card, FormField, Input, Spinner, useToast } from "@closer/ui";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import type { z } from "zod";

import { api, ApiRequestError } from "../../lib/apiClient";
import { useAuth } from "../../lib/authContext";
import { applyServerErrors, useZodForm } from "../../lib/useZodForm";

type ResendValues = z.infer<typeof resendVerificationSchema>;

/**
 * E-posta doğrulama ekranı (CLOS-359 `[S05]`).
 *
 * Üç durum, tek sayfa:
 * - **doğrulanıyor** — bağlantıyla gelindi, sunucudan cevap bekleniyor
 * - **başarılı** — hesap doğrulandı
 * - **bekliyor** — token yok ya da geçersiz/süresi dolmuş; yeniden gönderim yolu açılır
 *
 * Ayrı bir "süresi dolmuş" ekranı YOK ve bu bilinçli: kullanıcı için "bağlantı çalışmadı"
 * ile "bağlantı eskimiş" arasındaki fark eylemi değiştirmez — ikisinde de yapılacak şey
 * yeni bağlantı istemektir. Ayrıca sunucu da bu durumları AYIRT ETMİYOR (elinde token
 * olan birine hesap durumu sızmasın diye); arayüzün ayırt ediyormuş gibi davranması o
 * kararı boşa çıkarırdı.
 */
type Durum = "dogrulaniyor" | "basarili" | "bekliyor";

/**
 * Doğrulama ekranından çıkış (CLOS-496 Hata düzeltmesi).
 *
 * Bu ekran iki farklı kullanıcıya açılıyor ve "geri dön" ikisi için farklı anlama
 * geliyor:
 *
 * - **Oturumsuz** (e-postadaki bağlantıyla gelen): giriş sayfasına gitmek doğru → Link.
 * - **Girişli ama doğrulanmamış** (kayıt sonrası, `RequireVerifiedEmail` buraya getirdi):
 *   "Giriş sayfasına dön" /login'e, /login onu /tenants'a, `RequireVerifiedEmail` yeniden
 *   /verify-email'e atıyordu — sonsuz döngü, buton "çalışmıyor" görünüyordu. Bu
 *   kullanıcının tek anlamlı çıkışı OTURUMU KAPATMAK; çıkınca /login'de kalır.
 */
function GeriDon({ className }: { className?: string }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();

  // Oturumsuz (e-postadaki bağlantıyla gelen): giriş sayfası doğru hedef.
  if (!user) {
    return (
      <Link
        to="/login"
        className={className ?? "text-center font-medium text-primary hover:underline"}
      >
        Giriş sayfasına dön
      </Link>
    );
  }

  // Girişli (doğrulanmamış): GeriDon yalnız "bekliyor" durumunda kullanılıyor; orada
  // kullanıcı tanımı gereği doğrulanmamış. "Giriş sayfasına dön" /login → /tenants →
  // RequireVerifiedEmail → /verify-email döngüsü yapıyordu (CLOS-496). Tek anlamlı
  // çıkış oturumu kapatmak; çıkınca /login'de kalır. (Doğrulama başarılı durumu GeriDon
  // KULLANMIYOR — orası ayrı ele alınıyor, session'a bakmıyor.)
  const cikisYap = async (): Promise<void> => {
    try {
      await logout();
      navigate("/login");
    } catch {
      toast.error("Çıkış yapılamadı. Lütfen tekrar deneyin.");
    }
  };

  return (
    <Button variant="ghost" onClick={() => void cikisYap()}>
      Çıkış yap
    </Button>
  );
}

export function VerifyEmailPage() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [params] = useSearchParams();
  const token = params.get("token");
  const [durum, setDurum] = useState<Durum>(token ? "dogrulaniyor" : "bekliyor");
  const [gonderimMesaji, setGonderimMesaji] = useState<string | null>(null);

  /**
   * Doğrulama BAŞARILI olduktan sonra ileri gitme.
   *
   * Doğrulamanın başarısı POST 200 ile KESİN; oturumdaki `emailVerified` bayrağı henüz
   * bayat olabilir (ekranı bloklamamak için render anında tazelemiyoruz). Bu yüzden
   * GeriDon'un session'a bakan mantığına GÜVENMEK yerine burada net karar veriyoruz:
   * girişliyse oturumu tazeleyip uygulamaya sok (tazelemeden /tenants'a gitsek
   * RequireVerifiedEmail bayat bayrağı görüp /verify-email'e geri atardı), oturumsuzsa
   * giriş sayfasına.
   */
  const uygulamayaDevam = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ["session"] });
    navigate("/tenants");
  };

  // React 19 StrictMode geliştirmede efektleri İKİ KEZ çalıştırır. Koruma olmasaydı
  // token iki kez tüketilmeye çalışılır ve ikincisi "geçersiz" görünürdü — kullanıcı
  // geçerli bir bağlantıyla gelip hata ekranı görürdü.
  const denendi = useRef(false);

  useEffect(() => {
    if (!token || denendi.current) return;
    denendi.current = true;

    void (async () => {
      try {
        await api("/api/session/verify-email", {
          method: "POST",
          body: JSON.stringify({ token }),
        });
        // "Doğrulandı" ekranını HEMEN göster — oturumu burada tazelemek (await) ekranı
        // session refetch'i bitene kadar bloklardı ve yavaş/yoğun ağda "başarılı" durumu
        // geç gelirdi. Oturumdaki bayat `emailVerified`, ileri gidilen anda (butonda)
        // tazeleniyor; render bunu beklemiyor.
        setDurum("basarili");
      } catch {
        // Sunucu neden reddetti bilmiyoruz ve BİLMEMELİYİZ; kullanıcıya yapabileceği
        // şeyi gösteriyoruz.
        setDurum("bekliyor");
      }
    })();
  }, [token]);

  const form = useZodForm(resendVerificationSchema, {
    defaultValues: { email: "" } as ResendValues,
  });

  const onSubmit = form.handleSubmit(async (values) => {
    try {
      const { data } = await api<{ message: string }>("/api/session/resend-verification", {
        method: "POST",
        body: JSON.stringify(values),
      });
      // Metin SUNUCUDAN gelir. İstemci uydursaydı, sunucunun anti-enumeration ifadesiyle
      // ayrışabilir ve aradaki fark bilgi sızdırabilirdi.
      setGonderimMesaji(data.message);
    } catch (error) {
      applyServerErrors(form, error);
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : "İşlem tamamlanamadı. Lütfen tekrar deneyin.",
      );
    }
  });

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 p-4">
      <Card title="E-posta Doğrulama" className="w-full max-w-sm">
        {durum === "dogrulaniyor" && (
          <div role="status" className="flex flex-col items-center gap-3 py-4">
            <Spinner size="lg" />
            <p className="text-sm text-slate-600">Bağlantınız kontrol ediliyor…</p>
          </div>
        )}

        {durum === "basarili" && (
          <div className="flex flex-col gap-4">
            <p role="status" className="text-sm text-slate-600">
              E-posta adresiniz doğrulandı. Artık hesabınızı kullanmaya başlayabilirsiniz.
            </p>
            {/* Doğrulama KESİN başarılı; GeriDon'un session'a bakan mantığı burada bayat
                bayrak yüzünden yanlış karar verirdi. Girişliyse uygulamaya, değilse girişe. */}
            {user ? (
              <Button variant="ghost" onClick={() => void uygulamayaDevam()}>
                Uygulamaya devam et
              </Button>
            ) : (
              <Link to="/login" className="text-center font-medium text-primary hover:underline">
                Giriş sayfasına dön
              </Link>
            )}
          </div>
        )}

        {durum === "bekliyor" &&
          (gonderimMesaji ? (
            <div className="flex flex-col gap-4">
              <p role="status" className="text-sm text-slate-600">
                {gonderimMesaji}
              </p>
              {/* Form KAPANIR: sunucudaki cooldown ile aynı yöne çalışıyoruz, ona karşı
                  değil. Açık kalsaydı kullanıcı tekrar tıklar ve sessizce reddedilirdi. */}
              <GeriDon />
            </div>
          ) : (
            <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
              <p className="text-sm text-slate-500">
                {token
                  ? "Bağlantı geçersiz ya da süresi dolmuş. Yeni bir doğrulama bağlantısı isteyin."
                  : "Hesabınızın e-posta adresini girin; doğrulama bağlantısını gönderelim."}
              </p>
              <FormField label="E-posta" error={form.formState.errors.email?.message} required>
                <Input type="email" autoComplete="email" autoFocus {...form.register("email")} />
              </FormField>
              <Button type="submit" loading={form.formState.isSubmitting}>
                Bağlantıyı yeniden gönder
              </Button>
              <div className="text-center text-sm">
                <GeriDon className="font-medium text-primary hover:underline" />
              </div>
            </form>
          ))}
      </Card>
    </div>
  );
}
